import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { CustomLoadingIndicatorService } from '@frontend/sports/loading-indicator-feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { TrackingService } from '@frontend/sports/tracking/feature';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { UserService } from '@frontend/sports/user/feature';
import { LocalStoreService, SessionStoreService } from '@frontend/vanilla/core';
import { BetslipState } from 'packages/sports/common/betslip/core/betslip-state';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import { debounceTime, skip, take } from 'rxjs';

import { HelpBoxService } from '../help-box/help-box.service';
import { MyBetsEarlyPayoutService } from '../my-bets-base/early-payout/my-bets-earlypayout.service';
import { MyBetsEarlyPayoutServiceMyBetsFactory } from '../my-bets-base/early-payout/my-bets-earlypayout.service.factory';
import { ExportButtonType, IBetSlipRepositoryResult, MigrationTabType, MyBetsListContext } from '../my-bets-base/models/my-bets-viewmodels';
import { MyBetsSummaryService } from '../my-bets-base/my-bets-summary.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { PopupManager } from '../popup/popup-manager.service';
import { MyBetsListService } from './components/betslip-list/my-bets-list.service';
import { MyBetsMigrationTab } from './models/my-bets-tab';
import { myBetsTrackingConstants } from './models/my-bets-tracking.model';
import { MyBetsSubnavigationResolveService } from './my-bets-subnavigation/my-bets-subnavigation.service';
import { MyBetsViewBaseComponent } from './my-bets-view-base.component';
import { AssociatedAccountService } from './services/associated-account-service';
import { MyBetsBetslipRepository } from './services/my-bets-betslip.repository';
import { MainMyBetsStrategy, MyBetsStrategy } from './services/my-bets-display.strategy';
import { MyBetsEditBetHistoryService } from './services/my-bets-edit-bet-history.service';
import { MyBetsMigrationTabsService } from './services/my-bets-migration-tabs.service';
import { MyBetsUtilService } from './services/my-bets-util.service';
import { StadiumBetsVisibilityService } from './services/stadium-bets-visibility.service';

@Component({
    selector: 'ms-my-bets-view',
    templateUrl: 'my-bets-view.html',
    providers: [{ provide: MyBetsStrategy, useClass: MainMyBetsStrategy }],
    standalone: false,
})
export class MyBetsViewComponent extends MyBetsViewBaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input() context = 'document';
    @Input() inputData: IBetSlipRepositoryResult;
    @Input() isInitalLoadReadyFromRouteComponent: boolean;

    myBetsListContext = new MyBetsListContext();
    earlyPayoutService: MyBetsEarlyPayoutService;

    private lastReloadTimeStamp: number;
    private lastOpenBetsCount: number;
    private lastSelectedMigrationTabStorageKey = 'lastSelectedMigrationTab';

    constructor(
        editBetHistoryService: MyBetsEditBetHistoryService,
        userService: UserService,
        trackingService: TrackingService,
        repository: MyBetsBetslipRepository,
        mybetsConfig: MyBetsConfig,
        helpBoxService: HelpBoxService,
        localStorage: LocalStoreService,
        loadingIndicatorService: CustomLoadingIndicatorService,
        earlyPayoutServiceFactory: MyBetsEarlyPayoutServiceMyBetsFactory,
        myBetsListService: MyBetsListService,
        sitecoreMyBets: SitecoreMyBetsConfig,
        public override popupManager: PopupManager,
        public redirectHelperService: RedirectHelperService,
        private navigationRouter: NavigationRouterService,
        private mybetsSummary: MyBetsSummaryService,
        private migrationTabService: MyBetsMigrationTabsService,
        associatedAccountService: AssociatedAccountService,
        private sessionStorage: SessionStoreService,
        private myBetsSubnavigationResolveService: MyBetsSubnavigationResolveService,
        private betslipIntegrationService: BetslipIntegrationService,
        protected override changeRef: ChangeDetectorRef,
        stadiumMyBetsService: StadiumBetsVisibilityService,
        public myBetsUtilService: MyBetsUtilService,
    ) {
        super(
            loadingIndicatorService,
            '',
            repository,
            mybetsConfig,
            helpBoxService,
            localStorage,
            editBetHistoryService,
            userService,
            trackingService,
            myBetsListService,
            false,
            sitecoreMyBets,
            popupManager,
            associatedAccountService,
            stadiumMyBetsService,
            changeRef,
        );
        this.myBetsListContext.isEventView = false;
        this.earlyPayoutService = earlyPayoutServiceFactory.create();
        this.setLastReloadTimeStamp();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.lastOpenBetsCount = this.mybetsSummary.summary.openBetsCount;

        this.betslipIntegrationService
            .betslipState$()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((state) => {
                if (
                    state === BetslipState.Result &&
                    (this.inputData.listType === BetslipListType.Open || this.inputData.listType === BetslipListType.Live)
                ) {
                    this.tryReload();
                }
            });

        this.mybetsSummary.summaryUpdate.pipe(debounceTime(this.reloadTimeInterval), takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
            if (!state.isMigrationSummary && state.openBetsCount !== this.lastOpenBetsCount) {
                this.lastOpenBetsCount = state.openBetsCount;
                this.tryReload();
            }
        });

        this.myBetsListContext.listType = this.inputData.listType!;
        this.showMoreVisible = this.inputData.hasMore;

        if (this.showMigrationTabs) {
            this.selectMigrationTab();
            this.subscribeToMigrationTabSelected();
        }

        this.myBetsListContext.showMigrationTabs = this.showMigrationTabs;
    }

    ngOnChanges(): void {
        this.isInitalLoadReady = this.isInitalLoadReadyFromRouteComponent;

        this.navigationRouter.resetNavigation();
        this.navigationRouter.setNavigation(myBetsTrackingConstants.MY_BETS);

        this.data = this.inputData;
        this.myBetsListContext.listType = this.inputData.listType!;

        const isSettled = this.myBetsListContext.listType === BetslipListType.Settled;
        this.myBetsListContext.showFilter = isSettled;
        if (isSettled) {
            const buttonType = this.mybetsConfig?.exportButtonType?.myBets;
            if (buttonType) {
                this.myBetsListContext.showPrintButton = buttonType === ExportButtonType.Print;
                this.myBetsListContext.showDownloadButton = buttonType === ExportButtonType.Download;
            }
        }

        this.showMoreVisible = this.inputData.hasMore;

        this.isAutoCashoutHelpBoxVisible = this.getIsAutoCashoutHelpBoxVisible();
    }

    ngOnDestroy(): void {
        this.sessionStorage.remove(this.lastSelectedMigrationTabStorageKey);
    }

    get columns(): number {
        return this.myBetsListService.columns;
    }

    tryReload = async (): Promise<void> => {
        if (this.lastReloadTimeStamp + this.reloadTimeInterval < DateProviderService.now()) {
            this.setLastReloadTimeStamp();
            await this.reload();
        }
    };

    private get reloadTimeInterval(): number {
        return this.mybetsConfig.reloadMyBetsTimeInterval || 2000;
    }

    private setLastReloadTimeStamp(): void {
        this.lastReloadTimeStamp = DateProviderService.now();
    }

    private subscribeToMigrationTabSelected(): void {
        this.migrationTabService.tabSelected.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.onMigrationTabChange);
    }

    private selectMigrationTab(): void {
        if (!this.migrationTabType) {
            this.migrationTabType = MigrationTabType.After;
        }
        this.myBetsListContext.migrationTabType = this.migrationTabType;
        this.myBetsListContext.associatedAccount = this.migrationTabType === MigrationTabType.Before ? this.associatedAccount : null;
        this.migrationTabService.selectTab(this.migrationTabType);
    }

    private onMigrationTabChange = (tab: MyBetsMigrationTab) => {
        this.migrationTabType = tab.typeFilter;
        this.myBetsListContext.associatedAccount = this.migrationTabType === MigrationTabType.Before ? this.associatedAccount : null;
        this.sessionStorage.set(this.lastSelectedMigrationTabStorageKey, tab.typeFilter);
        this.myBetsListContext.migrationTabType = tab.typeFilter;
        this.updateBetsCount();
        this.reload();
    };

    private updateBetsCount(): void {
        this.mybetsSummary.summaryUpdate.pipe(skip(1), take(1)).subscribe((summary) => {
            this.myBetsSubnavigationResolveService.build(summary);
        });
    }
}
